import { formatNumericValue } from 'utils/numbers'
import { ReactNode } from 'react'
import { clsx } from 'clsx'

interface TabButtonsProps<T extends string> {
  activeValue: T
  values: readonly (readonly [T, number])[]
  onChange: (x: T) => void
  renderLabel?: (value: T) => string | ReactNode
  showBorders?: boolean
  rounded?: boolean
  fillWidth?: boolean
  className?: string
}

const defaultRenderLabel = <T extends string>(value: T) => value

const TabButtons = <T extends string>({
  activeValue,
  values,
  onChange,
  renderLabel = defaultRenderLabel<T>,
  showBorders = false,
  rounded = false,
  fillWidth = false,
  className = '',
}: TabButtonsProps<T>) => {
  return (
    <div
      className={clsx(
        'flex w-full border-b border-th-bkg-3 text-th-fgd-4',
        rounded ? 'rounded-lg' : 'rounded-t-xl md:rounded-t-2xl',
      )}
    >
      {values.map(([value, count], i) => (
        <div className={clsx({ 'flex-1': fillWidth })} key={`${value}${i}`}>
          <button
            className={clsx(
              'flex h-12 w-full items-center justify-center px-4 focus-visible:bg-th-bkg-2 focus-visible:text-th-fgd-1 md:px-6',
              className,
              rounded ? 'rounded-md' : 'rounded-none',
              showBorders &&
                (fillWidth && i === values.length - 1
                  ? 'border-r-0'
                  : 'border-r border-th-bkg-3'),
              value === activeValue
                ? value === 'buy'
                  ? 'bg-th-up-dark text-th-fgd-1'
                  : value === 'sell'
                    ? 'bg-th-down-dark text-th-fgd-1'
                    : 'bg-th-bkg-3 text-th-fgd-1'
                : 'hover:cursor-pointer hover:text-th-fgd-2',
              {
                'rounded-tl-lg md:rounded-tl-xl': i === 0,
                'rounded-tr-lg md:rounded-tr-xl':
                  fillWidth && i === values.length - 1,
              },
            )}
            onClick={() => onChange(value)}
          >
            <span
              className={clsx(
                value === 'buy' || value === 'sell'
                  ? 'font-display'
                  : 'font-semibold',
                'whitespace-nowrap',
              )}
            >
              {renderLabel(value)}
            </span>
            {count ? (
              <div
                className={clsx(
                  'ml-1.5 rounded',
                  value === activeValue ? 'bg-th-bkg-1' : 'bg-th-bkg-3',
                  'px-1.5 font-mono text-xxs text-th-fgd-2',
                )}
              >
                {formatNumericValue(count)}
              </div>
            ) : null}
          </button>
        </div>
      ))}
    </div>
  )
}

export default TabButtons
